import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const getBaseURL = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  return process.env.NODE_ENV === 'production' ? '/api/v1' : 'http://localhost:8000/api/v1';
};

export const createAPI = (getAccessToken, sessionStatus) => {
  const api = axios.create({
    baseURL: getBaseURL(),
  });

  api.interceptors.request.use(async (config) => {
    // Don't intercept if this is a retry and session is expired
    if (config._retry && sessionStatus === 'expired') {
      throw new Error('Session expired');
    }

    try {
      const tokens = await getAccessToken();
      console.log('[API] Got tokens:', {
        hasAccessToken: !!tokens?.access_token,
        accessToken: tokens?.access_token?.slice(0, 10) + '...',
        hasRefreshToken: !!tokens?.refresh_token,
        url: config.url
      });
      if (!tokens?.access_token) {
        throw new Error('No valid session');
      }
      
      config.headers.Authorization = `Bearer ${tokens.access_token}`;
      if (tokens.refresh_token) {
        config.headers['X-Refresh-Token'] = tokens.refresh_token;
      }
      console.log('[API] Final request headers:', {
        auth: config.headers.Authorization?.slice(0, 20) + '...',
        refresh: config.headers['X-Refresh-Token']?.slice(0, 10) + '...',
        url: config.url
      });
      return config;
    } catch (error) {
      console.error('[API] Request interceptor error:', error);
      throw error;
    }
  });

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      console.error('[API] Error:', {
        message: error.message,
        status: error.response?.status,
        url: originalRequest.url,
      });
      
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const tokens = await getAccessToken();
          if (tokens?.access_token) {
            originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`;
            if (tokens.refresh_token) {
              originalRequest.headers['X-Refresh-Token'] = tokens.refresh_token;
            }
            return api(originalRequest);
          }
        } catch (refreshError) {
          console.error('[API] Token refresh failed:', refreshError);
        }
      }
      
      return Promise.reject(error);
    }
  );

  return api;
};

// Hook to use the API instance
export const useAPI = () => {
  const { getAccessToken, sessionStatus } = useAuth();
  return createAPI(getAccessToken, sessionStatus);
};
